/**
 * React Query hooks for the Combined Funnel analytics endpoint (F1).
 *
 * GET /api/v1/analytics/funnel — tenantContext resolved upstream by middleware.
 */
import { useAuth } from "@clerk/clerk-react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { ApiError, FunnelResponse } from "@/lib/api-client";
import { fetchFunnelAnalytics } from "@/lib/api-client";

export const analyticsKeys = {
  all: ["analytics"] as const,
  funnel: () => ["analytics", "funnel"] as const,
} as const;

export type UseFunnelAnalyticsResult = UseQueryResult<FunnelResponse, ApiError | Error>;

/**
 * Hook for the combined funnel: content → clicks → leads → bookings.
 *
 * Returns the raw `FunnelResponse` (tenantContext scoped by the API). Callers
 * can derive summary totals by summing over `by_post`.
 *
 * Pass `enabled: false` to skip the fetch when the caller knows the user lacks
 * content module access (mirrors the `leadsAnalytics` pattern in the dashboard).
 */
export function useFunnelAnalytics(opts: { enabled?: boolean } = {}): UseFunnelAnalyticsResult {
  const { getToken } = useAuth();
  return useQuery<FunnelResponse, ApiError | Error>({
    queryKey: analyticsKeys.funnel(),
    queryFn: () => fetchFunnelAnalytics({ getToken: () => getToken() }),
    staleTime: 60_000,
    ...(opts.enabled !== undefined ? { enabled: opts.enabled } : {}),
  });
}
